/**
 * API client for documentation backend (api.tablemate.net).
 * Uses credentials: 'include' for session cookies.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.tablemate.net';

let loadingCount = 0;
let loadingListener = null;

function notifyLoading() {
  if (loadingListener) loadingListener(loadingCount > 0);
}

function withLoading(promise) {
  loadingCount++;
  notifyLoading();
  return promise.finally(() => {
    loadingCount--;
    notifyLoading();
  });
}

function request(path, options = {}, skipLoading = false) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const opts = {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  };
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    opts.body = JSON.stringify(options.body);
  }
  const p = fetch(url, opts).then(async (res) => {
    const text = await res.text();
    const data = text.length ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok) throw { status: res.status, error: data.error || res.statusText, ...data };
    return data;
  });
  return skipLoading ? p : withLoading(p);
}

export const api = {
  setLoadingListener: (fn) => { loadingListener = fn; },
  /** Raw fetch with loading overlay (for external URLs like fetchDocArticles, search) */
  fetch: (url, options) => withLoading(fetch(url, options)),
  /** GET without loading overlay (e.g. tag suggestions) */
  getSilent: (path) => request(path, { method: 'GET' }, true),
  get: (path) => request(path, { method: 'GET' }),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  delete: (path) => request(path, { method: 'DELETE' }),
  /** Upload a file (FormData). Do not set Content-Type so browser sets multipart boundary. */
  upload: (path, formData) => {
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const p = fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      const text = await res.text();
      const data = text.length ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) throw { status: res.status, error: data.error || res.statusText, ...data };
      return data;
    });
    return withLoading(p);
  },
};

export default api;
